import { IContractValue } from "@components/global/new/ContractSelector/ContractSelector.props";
import { AdminParamSettingsI } from "@services/changeMinerSettings";
import { IMiningPlanOptions, MINER_SETTINGS_E } from "@services/getMinerList";
import { UserI } from "@services/getUsers";
import {
  MinerCustomerEvent,
  MinerDefaultSpeedEvent,
  MinerTypeEvent,
} from "@services/http/socket/responses";
import { RootState } from "@store";
import { getFormValues } from "@utils/getFormValue";
import { useContracts } from "@utils/hooks/useContracts";
import { useMinerLocations } from "@utils/hooks/useMinerLocations";
import { useMinerProfiles } from "@utils/hooks/useMinerProfiles";
import { useUsers } from "@utils/hooks/useUsers";
import { useDispatch, useSelector } from "react-redux";
import { useMinerContext } from "../MinerRow/MinerContext";
import { MyFormMinerSettings } from "../MinerRow/MinerRowMore/MyForm";
import { IContractAndPlan, MinerActions } from "../MinerRow/minerReducer";
import useSettingsChange from "../useSettingsChange";
import { makeNotifs } from "@utils/makeNotifs";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import { dev } from "@utils/dev";
import { useCallback, useEffect } from "react";
import {
  getRangesFor,
  isTimeInPeriods,
} from "@components/PlanCreator/Periods.utils";
import { show } from "redux-modal";
import { MODALS_ENUM } from "@constants/Modals";
import { useIntl } from "react-intl";
import { useMiningPlans } from "@utils/hooks/useMiningPlans";

const emptyContract = {
  id: -1,
  name: "",
  password: "",
  meta: {},
  files: "",
  plans: "",
} as IContractValue;

const emptyPlan = {
  id: -1,
  name: "",
  schedule: [],
  meta: "",
} as IMiningPlanOptions;

type SettingsParams = {
  [key in MINER_SETTINGS_E]?: string;
} & {
  prev_plan?: string; // т.к. приходит из формы, в виде строки
};

export function useAdminSettingsControl(
  minerId: string // id майнеров или 1го майнера
) {
  const {
    singleSetting,
    onChange,
    deleteContractFromMiner,
    removePlanFromMiner,
  } = useSettingsChange(minerId);
  const { dispatchMiner } = useMinerContext();
  const dispatch = useDispatch();
  const locations = useMinerLocations();
  //const users = useUsers();
  const miningPlans = useMiningPlans();
  const contracts = useContracts();
  const minerProfiles = useMinerProfiles();
  const { formatMessage } = useIntl();

  const doChangeSettings = useCallback((
    params: SettingsParams,
    user?: UserI
  ) => {

    //console.log("doChangeSettings", params);

    if (params["mining_persent"] === "") {
      params["mining_persent"] = "0";
    }
    params["miner"] = minerId.toString();

    for (let key in params) {
      if (
        !params[key as keyof typeof params] &&
        key !== "contractid" &&
        key !== "miner_plan"
      ) {
        delete params[key as keyof typeof params];
      } else {
        switch (key) {
          // убрал отсюда обработку контракта и плана, т.к. там сложнее
          case MINER_SETTINGS_E.MINER_ID:
            dispatchMiner({
              type: MinerActions.MinerId,
              payload: params[MINER_SETTINGS_E.MINER_ID] || ""
            });
            break;

          case MINER_SETTINGS_E.MINER_LOCATION:
            params["location_id"] = params[MINER_SETTINGS_E.MINER_LOCATION];
            const location = locations?.find((l) => {
              return Number(l.Id) === Number(params["location_id"]);
            });
            if (location) {
              dispatchMiner({
                type: MinerActions.Location,
                payload: {
                  name: location.Name,
                  address: location.Address,
                  id: "" + location.Id,
                  location_id: location.Code,
                }
              });
            }
            delete params["miner_location"];
            break;

          case MINER_SETTINGS_E.MINER_PROFILE:
            const currProf = minerProfiles.find(
              (p) => Number(p.id) === Number(params.profile_id)
            );
            if (currProf) {
              const data: MinerTypeEvent = {
                id: "",
                created: 0,
                type: currProf,
              };
              dispatchMiner({
                type: MinerActions.MinerType,
                payload: data
              });
            } else {
              console.error(
                "Cannot find profile with id = ",
                params.profile_id,
                minerProfiles
              );
            }
            break;

          case MINER_SETTINGS_E.POOL:
            dispatchMiner({
              type: MinerActions.Settings,
              payload: params.pool_id || ""
            });
            break;

          case MINER_SETTINGS_E.SPEED_DEFAULT:
            dispatchMiner({
              type: MinerActions.DefaultSpeed,
              payload: {
                id: "",
                created: Date.now(),
                speed: +(params.default_speed || 0),
              } as MinerDefaultSpeedEvent
            });
            break;

          case MINER_SETTINGS_E.USER_CODE:
            if (params[MINER_SETTINGS_E.USER_CODE] === "0000-0000") {
              break;
            }
            if (user) {
              dispatchMiner({
                type: MinerActions.Customer,
                payload: {
                  customer_id: user.id,
                  customer_uuid: user.Uuid,
                  customer_email: user.email,
                  customer_name: user.name,
                } as MinerCustomerEvent
              });
            }
            // const user = users.find(
            //   (u) => u.Uuid === params[MINER_SETTINGS_E.USER_CODE]
            // ) as UserI;

            break;

          case MINER_SETTINGS_E.ADMIN_PERCENT:
            dispatchMiner({
              type: MinerActions.Percent,
              payload: params.mining_persent || "0"
            });
            break;

          default:
            break;
        }
      }
    }

    const hasContract = hasParam(MINER_SETTINGS_E.MINER_CONTRACT);
    const hasPlan = hasParam(MINER_SETTINGS_E.MINER_PLAN);

    if (hasContract && hasPlan) {
      dispatchMiner({
        type: MinerActions.ContractAndPlan,
        payload: {
          plan: getPlanPayload(),
          contract: getContractPayload(),
        } as IContractAndPlan
      });
    } else {
      if (hasContract) {
        dispatchMiner({
          type: MinerActions.Contract,
          payload: getContractPayload()
        });
      }
      if (hasPlan) {
        // console.log("Dispatch plan", {
        //   type: MinerActions.Plan,
        //   payload: getPlanPayload()
        // });
        dispatchMiner({
          type: MinerActions.Plan,
          tag: "SETTINGS",
          payload: getPlanPayload()
        });
      }
    }

    if (
      params.contractid === "" ||
      params.contractid === "0" ||
      params.contractid === "-1"
    ) {
      deleteContractFromMiner(params.miner);
      delete params.contractid;
    }
    if (
      (["", "-1"].indexOf(params.miner_plan ?? "") !== -1)
    ) {
      if (params.prev_plan !== "-1") {
        removePlanFromMiner(params.miner);
      }
      delete params.miner_plan;
    }
    delete params.prev_plan;

    function getContractPayload(): IContractValue {
      if (params.contractid !== "0" && params.contractid !== "-1") {
        const contract = contracts.find(
          (c) => c.id === Number(params.contractid)
        );
        if (contract) {
          return contract;
        }
      }
      return emptyContract;
    }

    function getPlanPayload(): IMiningPlanOptions {
      const plan = miningPlans?.find(
        (p) => Number(p.id) === Number(params[MINER_SETTINGS_E.MINER_PLAN])
      );
      if (plan) return plan;
      return emptyPlan;
    }

    function hasParam(
      key: keyof AdminParamSettingsI | MINER_SETTINGS_E
    ): boolean {
      return params.hasOwnProperty(key);
    }

    if (Object.keys(params).length === 1
      && Object.keys(params)[0].toLocaleLowerCase() === "miner")
      return;

    onChange(params);
  }, [miningPlans]);

  const removeUserSettings = useCallback((userId: number) => {
    dispatchMiner({
      type: MinerActions.AllSettingsRemoveByUserId,
      payload: userId,
    });
  }, []);

  const onSubmit = useCallback(
    (value: { [key: string]: string }, user?: UserI) => {

      // если userUuid = "0000-0000", то передается майнер админу (забирается у клиента)
      const params: SettingsParams = {
        ...value,
      };

      const prevPlan = params.prev_plan;
      //delete params.prev_plan;
      if (prevPlan !== "-1" && prevPlan !== "3" && prevPlan !== undefined) {
        if (
          // params.contractid === "0" ||
          params.contractid === "-1" ||
          params.contractid === "" ||
          params.miner_plan === "-1" ||
          params.miner_plan === ""
        ) {
          const plan = miningPlans?.find((p) => p.id === parseInt(prevPlan));
          if (plan) {
            const ranges = getRangesFor(new Date(), plan.schedule);
            if (isTimeInPeriods(new Date(), ranges)) {
              // значит майнер выключен
              dispatch(
                show(MODALS_ENUM.CONFIRM_MODAL, {
                  title: formatMessage({
                    id: "machines.more.setinngs.confirm.remove_plan.title",
                  }),
                  subtitle: formatMessage({
                    id: "machines.more.setinngs.confirm.remove_plan.subtitle",
                  }),
                  onConfirm() {
                    doChangeSettings(params);
                  },
                  onCancel() { },
                })
              );
              return;
            }
          }
        }
      }
      doChangeSettings(params);
    },
    [miningPlans]
  );

  return {
    settingsLoader: singleSetting || null,
    removeUserSettings,
    onSubmit,
  };
}
